import React from 'react';
import UsageStrip from './UsageStrip';

/**
 * 24/7 Auto-Apply tab content. Server-side queue powered by Greenhouse,
 * Lever, Workable, and Ashby public APIs.
 *
 * Renders:
 *   - Section header
 *   - Refresh + pause buttons
 *   - UsageStrip (plan badge + daily/hourly/discoveries counters)
 *   - Mode / threshold / status filter controls
 *   - Skeleton loader while fetching
 *   - Empty state (varies by status filter + plan)
 *   - Job table (when jobs are loaded)
 *
 * Pure presentation. Parent owns all queue state + handlers.
 */
export default function QueueTab({
  // Data
  usage,
  resetCountdown,
  queueJobs,
  queueLoading,
  queueRefreshing,

  // Settings
  queueMode,
  setQueueMode,
  queueThreshold,
  setQueueThreshold,
  queueStatusFilter,
  setQueueStatusFilter,
  queueSettingsSavedAt,

  // Handlers
  refreshQueue,
  togglePause,
  saveQueueSettings,
  decideJob,

  // Navigation (for empty-state CTAs)
  navigate,
  setActiveTab,
}) {
  const discoveryAtCap = usage?.discoveries && !usage.discoveries.allowed;

  return (
    <div key="tab-queue" className="ajp-tab-pane">

      {/* Section header */}
      <div className="ajp-section-header">
        <div className="ajp-section-header-icon">🌙</div>
        <div>
          <div className="ajp-section-header-title">24/7 Auto-Apply</div>
          <div className="ajp-section-header-sub">
            We discover, score, and apply in the background. Close your laptop — we keep going.
          </div>
        </div>
      </div>

      {/* Approval queue card */}
      <div style={{ marginTop: 8, padding: 20, border: '1px solid rgba(224,163,255,0.25)', borderRadius: 12, background: 'rgba(20,10,40,0.4)' }}>

        {/* Header row: title + controls */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#e0a3ff' }}>
            📥 Job Queue · Greenhouse · Lever · Workable · Ashby
          </h3>
          <div style={{ display: 'flex', gap: 8 }}>
            {usage && (
              <button onClick={togglePause}
                style={{ padding: '8px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                         background: usage.queuePaused ? '#facc15' : 'transparent',
                         color: usage.queuePaused ? '#0a0a0a' : '#facc15',
                         border: '1px solid #facc15', cursor: 'pointer' }}>
                {usage.queuePaused ? '▶ Resume queue' : '⏸ Pause queue'}
              </button>
            )}
            <button
              onClick={refreshQueue}
              disabled={queueRefreshing || discoveryAtCap}
              title={discoveryAtCap ? `Daily discovery cap reached (${usage.discoveries.used}/${usage.discoveries.limit}). Resets at 00:00 UTC.` : ''}
              style={{
                padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                background: queueRefreshing ? '#555' : discoveryAtCap ? '#444' : '#7c3aed',
                color: '#fff', border: 'none',
                cursor: queueRefreshing ? 'wait' : discoveryAtCap ? 'not-allowed' : 'pointer',
                opacity: discoveryAtCap ? 0.6 : 1,
              }}>
              {queueRefreshing
                ? '🔄 Discovering…'
                : discoveryAtCap
                  ? `🛑 Daily cap (${usage.discoveries.used}/${usage.discoveries.limit})`
                  : '🔄 Refresh Queue'}
            </button>
          </div>
        </div>

        {/* Plan / counters / countdown strip */}
        <UsageStrip usage={usage} resetCountdown={resetCountdown} />

        {/* Mode + threshold + status filter */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap', fontSize: 13 }}>
          <label style={{ color: 'rgba(224,163,255,0.7)' }}>Mode:</label>
          <select value={queueMode}
            onChange={e => { setQueueMode(e.target.value); saveQueueSettings(e.target.value, queueThreshold); }}
            style={{ padding: '6px 10px', borderRadius: 6, background: 'rgba(255,255,255,0.05)', color: '#fff', border: '1px solid rgba(255,255,255,0.15)' }}>
            <option value="review">Review every job</option>
            <option value="hybrid">Hybrid (auto-apply if score ≥ threshold)</option>
            <option value="auto">Auto-apply (no review)</option>
          </select>

          <label style={{ color: 'rgba(224,163,255,0.7)', marginLeft: 8 }}>Threshold:</label>
          <input type="number" min="0" max="100" value={queueThreshold}
            onChange={e => setQueueThreshold(parseInt(e.target.value, 10) || 0)}
            onBlur={() => saveQueueSettings(queueMode, queueThreshold)}
            style={{ width: 70, padding: '6px 10px', borderRadius: 6, background: 'rgba(255,255,255,0.05)', color: '#fff', border: '1px solid rgba(255,255,255,0.15)' }} />

          <label style={{ color: 'rgba(224,163,255,0.7)', marginLeft: 8 }}>Show:</label>
          <select value={queueStatusFilter} onChange={e => setQueueStatusFilter(e.target.value)}
            style={{ padding: '6px 10px', borderRadius: 6, background: 'rgba(255,255,255,0.05)', color: '#fff', border: '1px solid rgba(255,255,255,0.15)' }}>
            <option value="waiting">Waiting</option>
            <option value="approved">Approved (in flight)</option>
            <option value="applied">Applied</option>
            <option value="failed">Failed</option>
            <option value="rejected">Rejected</option>
            <option value="">All</option>
          </select>

          {queueSettingsSavedAt && (
            <span style={{ marginLeft: 'auto', color: '#4ade80', fontWeight: 600, fontSize: 12 }}>
              ✓ Saved
            </span>
          )}
        </div>

        {/* Skeleton loader during fetch */}
        {queueLoading && (
          <div className="ajp-skel-table">
            {[0, 1, 2, 3, 4].map(i => (
              <div key={i} className="ajp-skel-row">
                <div className="ajp-skeleton ajp-skel-cell tall" />
                <div className="ajp-skeleton ajp-skel-cell" />
                <div className="ajp-skeleton ajp-skel-cell" />
                <div className="ajp-skeleton ajp-skel-cell short" />
                <div className="ajp-skeleton ajp-skel-cell short" />
                <div className="ajp-skeleton ajp-skel-cell short" />
                <div className="ajp-skeleton ajp-skel-cell short" />
              </div>
            ))}
          </div>
        )}

        {/* Free-plan empty state — different copy than the per-status states */}
        {!queueLoading && usage?.plan === 'free' && queueJobs.length === 0 && (
          <div className="ajp-empty">
            <div className="ajp-empty-icon">🔒</div>
            <div className="ajp-empty-title">24/7 Auto-Apply isn't on your plan.</div>
            <div className="ajp-empty-sub">
              You're on the <strong>Free</strong> plan. Live Auto-Apply (LinkedIn / Dice / etc.)
              works fully — but background applies via Greenhouse · Lever · Workable · Ashby
              need a paid plan. <strong>Lite ($29.99/mo)</strong> unlocks 5 per day.
            </div>
            <div className="ajp-empty-actions">
              <button className="ajp-empty-btn" onClick={() => window.dispatchEvent(new CustomEvent('openPricingModal'))}>
                ✨ See plans
              </button>
              <button className="ajp-empty-btn secondary" onClick={() => setActiveTab('live')}>
                Use Live Auto-Apply instead
              </button>
            </div>
          </div>
        )}

        {/* Per-status empty states — only shown to paid users */}
        {!queueLoading && usage?.plan !== 'free' && queueJobs.length === 0 && (() => {
          const filter = queueStatusFilter || 'all';
          const config = {
            waiting:  {
              icon: '🔍',
              title: 'No jobs to review yet.',
              sub: 'Click Refresh Queue to discover matches across Greenhouse, Lever, Workable, and Ashby.',
              primary: { label: '🔄 Refresh Queue', onClick: refreshQueue, disabled: queueRefreshing },
              secondary: null,
            },
            approved: {
              icon: '⏳',
              title: 'No jobs in flight.',
              sub: 'When you approve a waiting job, it lands here while we apply on your behalf.',
              primary: { label: '← Back to waiting', onClick: () => setQueueStatusFilter('waiting') },
            },
            applied:  {
              icon: '🎯',
              title: 'No applications submitted yet.',
              sub: 'Approve a waiting job to start applying. Successful submissions appear here.',
              primary: { label: '← Back to waiting', onClick: () => setQueueStatusFilter('waiting') },
            },
            failed:   {
              icon: '✨',
              title: 'No failures — clean run.',
              sub: 'Failed applications would show up here with a reason. Nothing to see for now.',
              primary: { label: '← Back to waiting', onClick: () => setQueueStatusFilter('waiting') },
            },
            rejected: {
              icon: '🗑️',
              title: 'Nothing rejected.',
              sub: 'Jobs you reject from the waiting list will end up here for your records.',
              primary: { label: '← Back to waiting', onClick: () => setQueueStatusFilter('waiting') },
            },
            all: {
              icon: '📥',
              title: 'Your queue is empty.',
              sub: 'Click Refresh Queue to start. Your discovered, approved, applied, and rejected jobs will all show up here.',
              primary: { label: '🔄 Refresh Queue', onClick: refreshQueue, disabled: queueRefreshing },
              secondary: { label: 'Edit profile', onClick: () => navigate('/profile') },
            },
          };
          const c = config[filter] || config.waiting;
          return (
            <div className="ajp-empty">
              <div className="ajp-empty-icon">{c.icon}</div>
              <div className="ajp-empty-title">{c.title}</div>
              <div className="ajp-empty-sub">{c.sub}</div>
              <div className="ajp-empty-actions">
                {c.primary && (
                  <button className="ajp-empty-btn" onClick={c.primary.onClick} disabled={c.primary.disabled}>
                    {c.primary.label}
                  </button>
                )}
                {c.secondary && (
                  <button className="ajp-empty-btn secondary" onClick={c.secondary.onClick}>
                    {c.secondary.label}
                  </button>
                )}
              </div>
            </div>
          );
        })()}

        {/* Jobs table */}
        {queueJobs.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ textAlign: 'left', color: 'rgba(224,163,255,0.6)', borderBottom: '1px solid rgba(224,163,255,0.2)' }}>
                  <th style={{ padding: '8px 6px' }}>Score</th>
                  <th style={{ padding: '8px 6px' }}>Title</th>
                  <th style={{ padding: '8px 6px' }}>Company</th>
                  <th style={{ padding: '8px 6px' }}>Location</th>
                  <th style={{ padding: '8px 6px' }}>Platform</th>
                  <th style={{ padding: '8px 6px' }}>Status</th>
                  <th style={{ padding: '8px 6px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {queueJobs.map(j => (
                  <tr key={j.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                    <td style={{ padding: '8px 6px', fontWeight: 700, color: j.match_score >= 80 ? '#4ade80' : j.match_score >= 60 ? '#facc15' : '#f87171' }}>
                      {j.match_score ?? '—'}
                    </td>
                    <td style={{ padding: '8px 6px' }}>
                      <a href={j.apply_url} target="_blank" rel="noreferrer" style={{ color: '#fff', textDecoration: 'underline' }}>{j.title}</a>
                    </td>
                    <td style={{ padding: '8px 6px', color: 'rgba(255,255,255,0.8)' }}>{j.company}</td>
                    <td style={{ padding: '8px 6px', color: 'rgba(255,255,255,0.6)' }}>{j.location || '—'}</td>
                    <td style={{ padding: '8px 6px', color: 'rgba(255,255,255,0.6)' }}>{j.platform}</td>
                    <td style={{ padding: '8px 6px' }}>
                      <span style={{
                        padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600,
                        background: j.status === 'applied' ? 'rgba(74,222,128,0.2)' :
                                    j.status === 'failed'   ? 'rgba(248,113,113,0.2)' :
                                    j.status === 'rejected' ? 'rgba(160,160,160,0.2)' :
                                    j.status === 'approved' ? 'rgba(250,204,21,0.2)' :
                                                              'rgba(124,58,237,0.2)',
                        color:      j.status === 'applied' ? '#4ade80' :
                                    j.status === 'failed'   ? '#f87171' :
                                    j.status === 'rejected' ? '#a0a0a0' :
                                    j.status === 'approved' ? '#facc15' :
                                                              '#c4a3ff',
                      }}>{j.status}</span>
                      {j.status === 'failed' && j.failure_reason && (
                        <div title={j.failure_reason}
                             style={{ marginTop: 4, fontSize: 11, color: '#f87171', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {j.failure_reason}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '8px 6px' }}>
                      {j.status === 'waiting' && (() => {
                        const a = usage?.applies24_7 || { used: 0, limit: 0 };
                        const atCap = a.limit > 0 && a.used >= a.limit;
                        const blockedByPlan = a.limit === 0;
                        const approveTitle = blockedByPlan
                          ? 'Upgrade to Pro or Max to enable 24/7 auto-apply'
                          : atCap
                            ? 'Daily cap reached — will be queued for tomorrow'
                            : 'Approve and start applying';
                        return (
                          <>
                            <button onClick={() => decideJob(j.id, 'approve')}
                              disabled={blockedByPlan}
                              title={approveTitle}
                              style={{
                                marginRight: 6, padding: '4px 10px', borderRadius: 6,
                                fontSize: 12, fontWeight: 600,
                                background: blockedByPlan ? '#444' : atCap ? '#a3e635aa' : '#4ade80',
                                color: '#0a0a0a',
                                border: 'none', cursor: blockedByPlan ? 'not-allowed' : 'pointer',
                                opacity: blockedByPlan ? 0.5 : 1,
                              }}>
                              {blockedByPlan ? '🔒 Approve' : atCap ? 'Approve →tomorrow' : 'Approve'}
                            </button>
                            <button onClick={() => decideJob(j.id, 'reject')}
                              style={{ padding: '4px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600, background: 'transparent', color: '#f87171', border: '1px solid #f87171', cursor: 'pointer' }}>
                              Reject
                            </button>
                          </>
                        );
                      })()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}